import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../../shared/constants/theme';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { DataTable } from '../../shared';
import countryCodesData from '../../data/country-codes.json';
import countriesData from '../../data/countries-states-cities-data';
import { cityFilterOptions, countryFilterOptions, stateFilterOptions } from '../../utils/locationSearch';
import {
  createStockSupplier,
  deleteStockSupplier,
  getHotelConfiguration,
  getStockSuppliers,
  updateStockSupplier,
} from '../../services/api';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import {
  getStockListHeaderSubtitleSx,
  getStockListHeaderTitleSx,
  getStockListPaperSx,
} from '../../utils/stockMobileLayout';

const initialFormData = {
  name: '',
  email: '',
  phone: '',
  phone_country_code: '+91',
  address: '',
  country: '',
  state: '',
  city: '',
  gst_number: '',
  fssai_license_number: '',
  type: '',
  is_active: true,
};

const STOCK_TYPE_OPTIONS = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'sell', label: 'Sell' },
  { value: 'both', label: 'Both' },
];

const COUNTRY_OPTIONS = Array.isArray(countriesData?.countries) ? countriesData.countries : [];
const COUNTRY_CODE_OPTIONS = Array.isArray(countryCodesData) ? countryCodesData : [];
const SORTED_DIAL_CODES = [...COUNTRY_CODE_OPTIONS].sort(
  (a, b) => (b?.dialCode?.length || 0) - (a?.dialCode?.length || 0)
);

/** ITU-style upper bound for national significant number length (per selected country). */
const MAX_NATIONAL_DIGITS = 15;

/** Default when ISO is not mapped: permissive range for international numbers. */
const DEFAULT_NATIONAL_LENGTH_RANGE = { min: 6, max: MAX_NATIONAL_DIGITS };

/**
 * National subscriber number length bounds by ISO 3166-1 alpha-2 (matches country-codes.json `code`).
 * When absent, DEFAULT_NATIONAL_LENGTH_RANGE applies.
 */
const NATIONAL_LENGTH_RANGE_BY_ISO = {
  IN: { min: 10, max: 10 },
  US: { min: 10, max: 10 },
  CA: { min: 10, max: 10 },
  GB: { min: 10, max: 11 },
  AU: { min: 9, max: 9 },
  NZ: { min: 8, max: 11 },
  SG: { min: 8, max: 8 },
  AE: { min: 9, max: 9 },
  SA: { min: 9, max: 9 },
  QA: { min: 8, max: 8 },
  KW: { min: 8, max: 8 },
  BH: { min: 8, max: 8 },
  OM: { min: 8, max: 8 },
  DE: { min: 10, max: 11 },
  FR: { min: 9, max: 9 },
  IT: { min: 9, max: 11 },
  ES: { min: 9, max: 9 },
  NL: { min: 9, max: 9 },
  BE: { min: 9, max: 9 },
  CH: { min: 9, max: 12 },
  AT: { min: 10, max: 13 },
  SE: { min: 9, max: 10 },
  NO: { min: 8, max: 8 },
  DK: { min: 8, max: 8 },
  FI: { min: 6, max: 12 },
  IE: { min: 9, max: 9 },
  PT: { min: 9, max: 9 },
  GR: { min: 10, max: 10 },
  PL: { min: 9, max: 9 },
  CZ: { min: 9, max: 9 },
  SK: { min: 9, max: 9 },
  HU: { min: 9, max: 9 },
  RO: { min: 9, max: 9 },
  BR: { min: 10, max: 11 },
  MX: { min: 10, max: 10 },
  AR: { min: 10, max: 11 },
  CO: { min: 10, max: 10 },
  CL: { min: 9, max: 9 },
  PE: { min: 9, max: 9 },
  JP: { min: 10, max: 11 },
  KR: { min: 9, max: 11 },
  CN: { min: 11, max: 11 },
  HK: { min: 8, max: 8 },
  TW: { min: 9, max: 9 },
  MY: { min: 9, max: 11 },
  TH: { min: 9, max: 9 },
  ID: { min: 9, max: 12 },
  PH: { min: 10, max: 10 },
  VN: { min: 9, max: 10 },
  BD: { min: 10, max: 10 },
  PK: { min: 10, max: 10 },
  LK: { min: 9, max: 9 },
  NP: { min: 10, max: 10 },
  ZA: { min: 9, max: 9 },
  NG: { min: 10, max: 11 },
  EG: { min: 10, max: 10 },
  KE: { min: 9, max: 9 },
  TR: { min: 10, max: 10 },
  RU: { min: 10, max: 10 },
  UA: { min: 9, max: 9 },
  IL: { min: 9, max: 9 },
  JO: { min: 9, max: 9 },
  LB: { min: 7, max: 8 },
  IQ: { min: 10, max: 10 },
  IR: { min: 10, max: 10 },
};

const getNationalLengthRangeForCountry = (countryCodeOption) => {
  if (!countryCodeOption?.code) return DEFAULT_NATIONAL_LENGTH_RANGE;
  const iso = String(countryCodeOption.code).toUpperCase();
  return NATIONAL_LENGTH_RANGE_BY_ISO[iso] || DEFAULT_NATIONAL_LENGTH_RANGE;
};

const normalizePhoneInput = (value, maxDigits = MAX_NATIONAL_DIGITS) =>
  String(value || '')
    .replace(/\D/g, '')
    .slice(0, Math.min(maxDigits, MAX_NATIONAL_DIGITS));

const isNationalPhoneValid = (digits, countryCodeOption) => {
  const range = getNationalLengthRangeForCountry(countryCodeOption);
  const len = digits.length;
  return len >= range.min && len <= range.max;
};

const getMobileValidationHelperText = (digits, countryCodeOption) => {
  if (!digits.length) return '';
  if (isNationalPhoneValid(digits, countryCodeOption)) return '';
  const range = getNationalLengthRangeForCountry(countryCodeOption);
  const label = countryCodeOption?.name ? ` for ${countryCodeOption.name}` : '';
  if (range.min === range.max) {
    return `Mobile number must be ${range.min} digits${label}`;
  }
  return `Mobile number must be ${range.min}–${range.max} digits${label}`;
};

const resolveCountryNameFromHotelCode = (countryCode) => {
  if (!countryCode) return '';
  const normalizedCode = String(countryCode).trim().toUpperCase();
  const matchedCountry = COUNTRY_OPTIONS.find(
    (country) => country?.code?.toUpperCase() === normalizedCode
  );
  return matchedCountry?.name || '';
};

const inferCountryFromStateCity = (stateName, cityName) => {
  const normalizedState = String(stateName || '').trim().toLowerCase();
  const normalizedCity = String(cityName || '').trim().toLowerCase();

  for (const country of COUNTRY_OPTIONS) {
    const states = Array.isArray(country?.states) ? country.states : [];
    for (const state of states) {
      const stateMatch =
        normalizedState &&
        String(state?.name || '').trim().toLowerCase() === normalizedState;
      const cityMatch =
        normalizedCity &&
        Array.isArray(state?.cities) &&
        state.cities.some((city) => String(city || '').trim().toLowerCase() === normalizedCity);
      if (stateMatch || cityMatch) {
        return country?.name || '';
      }
    }
  }

  return '';
};

const splitPhoneWithDialCode = (rawPhone, fallbackDialCode = '+91') => {
  const phoneValue = String(rawPhone || '').trim();
  if (!phoneValue) {
    return { dialCode: fallbackDialCode, nationalNumber: '' };
  }

  const compactWithPlus = phoneValue.startsWith('+')
    ? `+${phoneValue.slice(1).replace(/\D/g, '')}`
    : '';
  if (compactWithPlus) {
    const matchedDialCode = SORTED_DIAL_CODES.find((country) =>
      compactWithPlus.startsWith(country.dialCode)
    );
    if (matchedDialCode) {
      return {
        dialCode: matchedDialCode.dialCode,
        nationalNumber: normalizePhoneInput(compactWithPlus.slice(matchedDialCode.dialCode.length)),
      };
    }
  }

  const digitsOnly = phoneValue.replace(/\D/g, '');
  if (digitsOnly) {
    const matchedFromDigits = SORTED_DIAL_CODES.find((country) => {
      const dialCodeDigits = country.dialCode.replace(/\D/g, '');
      if (!digitsOnly.startsWith(dialCodeDigits)) return false;
      const national = digitsOnly.slice(dialCodeDigits.length);
      if (!national.length) return false;
      const range = getNationalLengthRangeForCountry(country);
      return national.length >= range.min && national.length <= range.max;
    });
    if (matchedFromDigits) {
      const dialCodeDigits = matchedFromDigits.dialCode.replace(/\D/g, '');
      return {
        dialCode: matchedFromDigits.dialCode,
        nationalNumber: normalizePhoneInput(digitsOnly.slice(dialCodeDigits.length)),
      };
    }
  }

  return {
    dialCode: fallbackDialCode,
    nationalNumber: normalizePhoneInput(digitsOnly),
  };
};

const StockSupplierSection = ({ isDark }) => {
  const theme = useTheme();
  const chipBorder = isDark ? alpha('#ffffff', 0.08) : '#e0e0e0';
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff'),
    [isDark]
  );
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add');
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saveLoading, setSaveLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0,
  });
  const [sortConfig, setSortConfig] = useState({ column: 'name', direction: 'asc' });
  const [filters, setFilters] = useState({
    search: '',
    email: '',
    phone: '',
    type: '',
    status: '',
  });
  const [defaultCountryName, setDefaultCountryName] = useState('India');
  const [defaultDialCode, setDefaultDialCode] = useState('+91');
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const phoneCountryCodeAnchorRef = useRef(null);

  const isReadOnly = drawerMode === 'view';
  const hasName = Boolean(formData.name.trim());
  const hasEmailIfFilled = !String(formData.email || '').trim()
    ? true
    : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());

  const selectedPhoneCountryCode = useMemo(() => {
    return (
      COUNTRY_CODE_OPTIONS.find((country) => country.dialCode === formData.phone_country_code) ||
      COUNTRY_CODE_OPTIONS.find((country) => country.code === 'IN') ||
      COUNTRY_CODE_OPTIONS[0] ||
      null
    );
  }, [formData.phone_country_code]);

  const nationalPhoneRange = useMemo(
    () => getNationalLengthRangeForCountry(selectedPhoneCountryCode),
    [selectedPhoneCountryCode]
  );

  const nationalPhoneDigits = normalizePhoneInput(formData.phone);

  const hasPhone =
    nationalPhoneDigits.length > 0 &&
    isNationalPhoneValid(nationalPhoneDigits, selectedPhoneCountryCode);

  const mobileHelperText = getMobileValidationHelperText(
    nationalPhoneDigits,
    selectedPhoneCountryCode
  );

  const hasType = Boolean(formData.type);
  const hasCountry = Boolean((formData.country || '').trim());
  const hasState = Boolean((formData.state || '').trim());
  const hasCity = Boolean((formData.city || '').trim());
  const isFormValid =
    hasName &&
    hasPhone &&
    hasType &&
    hasEmailIfFilled &&
    hasCountry &&
    hasState &&
    hasCity;

  const selectedCountryObj = useMemo(() => {
    if (!formData.country) return null;
    return (
      COUNTRY_OPTIONS.find(
        (country) =>
          country?.name === formData.country ||
          country?.code === formData.country ||
          country?.name?.toLowerCase() === String(formData.country).toLowerCase()
      ) || null
    );
  }, [formData.country]);

  const availableStates = useMemo(() => {
    if (!selectedCountryObj) return [];
    return (selectedCountryObj.states || []).map((state) => ({
      code: state.code,
      name: state.name,
    }));
  }, [selectedCountryObj]);

  const selectedStateObj = useMemo(() => {
    if (!selectedCountryObj || !formData.state) return null;
    return (
      (selectedCountryObj.states || []).find(
        (state) =>
          state?.name === formData.state ||
          state?.code === formData.state ||
          state?.name?.toLowerCase() === String(formData.state).toLowerCase()
      ) || null
    );
  }, [formData.state, selectedCountryObj]);

  const stateOptions = useMemo(() => {
    const normalizedState = String(formData.state || '').trim().toLowerCase();
    if (!normalizedState) return availableStates;
    const exists = availableStates.some(
      (state) => String(state?.name || '').trim().toLowerCase() === normalizedState
    );
    if (exists) return availableStates;
    return [...availableStates, { code: formData.state, name: formData.state }];
  }, [availableStates, formData.state]);

  const availableCities = useMemo(() => {
    if (!selectedStateObj) return [];
    return Array.isArray(selectedStateObj.cities) ? selectedStateObj.cities : [];
  }, [selectedStateObj]);

  const cityOptions = useMemo(() => {
    const normalizedCity = String(formData.city || '').trim().toLowerCase();
    if (!normalizedCity) return availableCities;
    const exists = availableCities.some(
      (city) => String(city || '').trim().toLowerCase() === normalizedCity
    );
    if (exists) return availableCities;
    return [...availableCities, formData.city];
  }, [availableCities, formData.city]);

  const getFreshFormData = useCallback(
    (overrides = {}) => ({
      ...initialFormData,
      country: defaultCountryName || '',
      phone_country_code: defaultDialCode || '+91',
      ...overrides,
    }),
    [defaultCountryName, defaultDialCode]
  );

  const showMessage = (severity, message) => {
    setSnackbar({ open: true, severity, message });
  };

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const response = await getStockSuppliers();
      setSuppliers(response?.suppliers || []);
    } catch (error) {
      showMessage('error', error?.userMessage || 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuppliers();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadHotelLocationDefaults = async () => {
      try {
        const hotelConfig = await getHotelConfiguration();
        if (!isMounted) return;

        const hotelCountryCode = hotelConfig?.country || '';
        const resolvedCountryName = resolveCountryNameFromHotelCode(hotelCountryCode);
        const matchedDialCode = COUNTRY_CODE_OPTIONS.find(
          (country) => country?.code?.toUpperCase() === String(hotelCountryCode || '').toUpperCase()
        );

        if (resolvedCountryName) {
          setDefaultCountryName(resolvedCountryName);
        }
        if (matchedDialCode?.dialCode) {
          setDefaultDialCode(matchedDialCode.dialCode);
        }
      } catch (error) {
        // Keep India defaults when hotel configuration is unavailable.
      }
    };

    loadHotelLocationDefaults();
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredAndSortedSuppliers = useMemo(() => {
    const normalizedSearch = filters.search.trim().toLowerCase();
    const normalizedEmail = filters.email.trim().toLowerCase();
    const normalizedPhone = filters.phone.trim();
    let filtered = suppliers.filter((supplier) => {
      const matchesType = !filters.type || (supplier.type || '') === filters.type;
      const supplierStatus = supplier.is_active ? 'active' : 'inactive';
      const matchesStatus = !filters.status || supplierStatus === filters.status;
      const matchesEmail =
        !normalizedEmail || String(supplier.email || '').toLowerCase().includes(normalizedEmail);
      const matchesPhone =
        !normalizedPhone || String(supplier.phone || '').includes(normalizedPhone);

      const searchable = [
        supplier.name,
        supplier.company,
        supplier.email,
        supplier.phone,
        supplier.gst_number,
        supplier.fssai_license_number,
        supplier.state,
        supplier.type,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesSearch = !normalizedSearch || searchable.includes(normalizedSearch);
      return matchesType && matchesStatus && matchesSearch && matchesEmail && matchesPhone;
    });

    filtered.sort((a, b) => {
      const field = sortConfig.column;
      const aValue = String(a[field] ?? '').toLowerCase();
      const bValue = String(b[field] ?? '').toLowerCase();
      if (sortConfig.direction === 'asc') return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
    return filtered;
  }, [suppliers, filters, sortConfig]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, totalRecords: filteredAndSortedSuppliers.length }));
  }, [filteredAndSortedSuppliers.length]);

  const paginatedData = useMemo(() => {
    const start = pagination.page * pagination.perPage;
    const end = start + pagination.perPage;
    return filteredAndSortedSuppliers.slice(start, end);
  }, [filteredAndSortedSuppliers, pagination.page, pagination.perPage]);

  const openAddDrawer = () => {
    setDrawerMode('add');
    setSelectedSupplierId(null);
    setFormData(getFreshFormData());
    setDrawerOpen(true);
  };

  const openEditDrawer = (supplier) => {
    const phoneParts = splitPhoneWithDialCode(supplier.phone || '', defaultDialCode);
    const inferredCountry =
      inferCountryFromStateCity(supplier.state, supplier.city) || defaultCountryName || '';
    setDrawerMode('edit');
    setSelectedSupplierId(supplier.id);
    setFormData({
      name: supplier.name || '',
      email: supplier.email || '',
      phone: phoneParts.nationalNumber || '',
      phone_country_code: phoneParts.dialCode || defaultDialCode || '+91',
      address: supplier.address || '',
      country: inferredCountry,
      state: supplier.state || '',
      city: supplier.city || '',
      gst_number: supplier.gst_number || '',
      fssai_license_number: supplier.fssai_license_number || '',
      type: supplier.type || '',
      is_active: supplier.is_active ?? true,
    });
    setDrawerOpen(true);
  };

  const openViewDrawer = (supplier) => {
    const phoneParts = splitPhoneWithDialCode(supplier.phone || '', defaultDialCode);
    const inferredCountry =
      inferCountryFromStateCity(supplier.state, supplier.city) || defaultCountryName || '';
    setDrawerMode('view');
    setSelectedSupplierId(supplier.id);
    setFormData({
      name: supplier.name || '',
      email: supplier.email || '',
      phone: phoneParts.nationalNumber || '',
      phone_country_code: phoneParts.dialCode || defaultDialCode || '+91',
      address: supplier.address || '',
      country: inferredCountry,
      state: supplier.state || '',
      city: supplier.city || '',
      gst_number: supplier.gst_number || '',
      fssai_license_number: supplier.fssai_license_number || '',
      type: supplier.type || '',
      is_active: supplier.is_active ?? true,
    });
    setDrawerOpen(true);
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!isFormValid || isReadOnly) return;
    setSaveLoading(true);
    const normalizedPhone = normalizePhoneInput(formData.phone);
    const payload = {
      name: formData.name.trim(),
      company: '',
      email: formData.email.trim(),
      phone: `${formData.phone_country_code || defaultDialCode || '+91'}${normalizedPhone}`,
      address: formData.address.trim(),
      state: formData.state.trim(),
      city: formData.city.trim(),
      gst_number: formData.gst_number.trim(),
      fssai_license_number: formData.fssai_license_number.trim(),
      type: formData.type,
      tcs_percentage: 0,
      is_active: formData.is_active,
    };

    try {
      if (drawerMode === 'edit' && selectedSupplierId) {
        await updateStockSupplier(selectedSupplierId, payload);
        showMessage('success', 'Supplier updated successfully');
      } else {
        await createStockSupplier(payload);
        showMessage('success', 'Supplier created successfully');
      }
      setDrawerOpen(false);
      await loadSuppliers();
    } catch (error) {
      showMessage('error', error?.userMessage || 'Failed to save supplier');
    } finally {
      setSaveLoading(false);
    }
  };

  const openDeleteDialog = (supplier) => {
    setSupplierToDelete({ id: supplier.id, name: supplier.name || '' });
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setSupplierToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!supplierToDelete?.id) return;
    setDeleteLoading(true);
    try {
      await deleteStockSupplier(supplierToDelete.id);
      showMessage('success', 'Supplier deleted successfully');
      setDeleteDialogOpen(false);
      setSupplierToDelete(null);
      await loadSuppliers();
    } catch (error) {
      showMessage('error', error?.userMessage || 'Failed to delete supplier');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    { field: 'name', label: 'Name', sortable: true },
    { field: 'type', label: 'Type', sortable: true, render: (value) => (value || '-').toUpperCase() },
    { field: 'email', label: 'Email', sortable: true, render: (value) => value || '—' },
    { field: 'phone', label: 'Phone', sortable: true, render: (value) => value || '—' },
    { field: 'gst_number', label: 'GST Number', sortable: false, render: (value) => value || '—' },
    { field: 'fssai_license_number', label: 'FSSAI Number', sortable: false, render: (value) => value || '—' },
    {
      field: 'status',
      label: 'Status',
      sortable: false,
      render: (value, row) => (
        <Chip
          size="small"
          label={row.is_active ? 'ACTIVE' : 'INACTIVE'}
          sx={{
            backgroundColor: alpha(row.is_active ? '#4ce1b6' : '#9e9e9e', 0.14),
            color: row.is_active ? '#4ce1b6' : (isDark ? '#d1d5db' : '#6b7280'),
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      field: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'right',
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <IconButton size="small" onClick={() => openViewDrawer(row)} sx={{ color: theme.palette.text.secondary }}>
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => openEditDrawer(row)} sx={{ color: '#4ce1b6' }}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => openDeleteDialog(row)} sx={{ color: '#ef5350' }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const filterRowCells = columns.map((column) => {
    switch (column.field) {
      case 'name':
        return (
          <TextField
            key={column.field}
            placeholder="Search..."
            size="small"
            variant="standard"
            value={filters.search}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, search: e.target.value }));
              setPagination((prev) => ({ ...prev, page: 0 }));
            }}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: '100%' }}
          />
        );
      case 'type':
        return (
          <FormControl key={column.field} size="small" sx={{ width: '100%' }}>
            <Select
              value={filters.type}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, type: e.target.value }));
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
              displayEmpty
              variant="standard"
              disableUnderline
            >
              <MenuItem value="">All</MenuItem>
              {STOCK_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'email':
        return (
          <TextField
            key={column.field}
            placeholder="Filter email..."
            size="small"
            variant="standard"
            value={filters.email}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, email: e.target.value }));
              setPagination((prev) => ({ ...prev, page: 0 }));
            }}
            InputProps={{ disableUnderline: true }}
            sx={{ width: '100%' }}
          />
        );
      case 'phone':
        return (
          <TextField
            key={column.field}
            placeholder="Filter phone..."
            size="small"
            variant="standard"
            value={filters.phone}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, phone: e.target.value.replace(/\D/g, '') }));
              setPagination((prev) => ({ ...prev, page: 0 }));
            }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            InputProps={{ disableUnderline: true }}
            sx={{ width: '100%' }}
          />
        );
      case 'status':
        return (
          <FormControl key={column.field} size="small" sx={{ width: '100%' }}>
            <Select
              value={filters.status}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, status: e.target.value }));
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
              displayEmpty
              variant="standard"
              disableUnderline
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        );
      case 'actions':
        return (
          <Box key={column.field} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {(filters.search || filters.email || filters.phone || filters.type || filters.status) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setFilters({ search: '', email: '', phone: '', type: '', status: '' });
                  setPagination((prev) => ({ ...prev, page: 0 }));
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        );
      default:
        return <div key={column.field} />;
    }
  });

  return (
    <>
      <Paper elevation={0} sx={getStockListPaperSx(isDark)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '12px',
                backgroundColor: alpha('#4ce1b6', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocalShippingIcon sx={{ fontSize: 30, color: '#4ce1b6' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={getStockListHeaderTitleSx(isDark)}>
                Supplier List
              </Typography>
              <Typography variant="body2" sx={getStockListHeaderSubtitleSx(isDark)}>
                Manage stock suppliers
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDrawer}
            sx={{
              backgroundColor: '#4ce1b6',
              color: '#000',
              '&:hover': {
                backgroundColor: '#3ab89a',
                color: '#000',
              },
            }}
          >
            Add Supplier
          </Button>
        </Box>

        <DataTable
          columns={columns}
          data={paginatedData}
          loading={loading}
          pagination={pagination}
          onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
          onPerPageChange={(perPage) => setPagination((prev) => ({ ...prev, perPage, page: 0 }))}
          onSortChange={(newSort) => {
            setSortConfig(newSort);
            setPagination((prev) => ({ ...prev, page: 0 }));
          }}
          sortConfig={sortConfig}
          emptyMessage={
            suppliers.length === 0
              ? 'No suppliers found. Click "Add Supplier" to create one.'
              : 'No suppliers match the selected filters.'
          }
          stickyHeader
          filterRow={filterRowCells}
        />
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShippingIcon sx={{ color: '#4ce1b6', fontSize: 22 }} />
              <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
                {drawerMode === 'edit' ? 'Edit Supplier' : drawerMode === 'view' ? 'View Supplier' : 'Add Supplier'}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              size="small"
              sx={{ color: isDark ? '#999' : '#666', p: 0.5 }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  required
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile number"
                  required
                  value={formData.phone}
                  onChange={(e) =>
                    handleFieldChange(
                      'phone',
                      normalizePhoneInput(e.target.value, nationalPhoneRange.max)
                    )
                  }
                  disabled={isReadOnly}
                  helperText={mobileHelperText}
                  error={Boolean(mobileHelperText)}
                  inputProps={{
                    maxLength: nationalPhoneRange.max,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          ref={phoneCountryCodeAnchorRef}
                          onClick={() => {
                            if (!isReadOnly) setPhoneCountryCodeMenuOpen(true);
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: isReadOnly ? 'default' : 'pointer',
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            '&:hover': isReadOnly
                              ? {}
                              : {
                                  backgroundColor: isDark
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.05)',
                                },
                          }}
                        >
                          <Typography component="span" sx={{ fontSize: '1.1rem' }}>
                            {selectedPhoneCountryCode?.flag || '🇮🇳'}
                          </Typography>
                          <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                            {selectedPhoneCountryCode?.dialCode || '+91'}
                          </Typography>
                          {!isReadOnly && (
                            <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              ▼
                            </Typography>
                          )}
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Menu
                anchorEl={phoneCountryCodeAnchorRef.current}
                open={phoneCountryCodeMenuOpen}
                onClose={() => {
                  setPhoneCountryCodeMenuOpen(false);
                  setPhoneCountryCodeSearch('');
                }}
                PaperProps={{
                  sx: {
                    maxHeight: 400,
                    width: 300,
                  },
                }}
              >
                <Box sx={{ p: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search country..."
                    value={phoneCountryCodeSearch}
                    onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                </Box>
                {COUNTRY_CODE_OPTIONS
                  .filter((country) => {
                    if (!phoneCountryCodeSearch) return true;
                    const search = phoneCountryCodeSearch.toLowerCase();
                    return (
                      country.name.toLowerCase().includes(search) ||
                      country.dialCode.includes(search) ||
                      country.code.toLowerCase().includes(search)
                    );
                  })
                  .map((country) => (
                    <MenuItem
                      key={country.code}
                      onClick={() => {
                        handleFieldChange('phone_country_code', country.dialCode);
                        setPhoneCountryCodeMenuOpen(false);
                        setPhoneCountryCodeSearch('');
                      }}
                      selected={selectedPhoneCountryCode?.dialCode === country.dialCode}
                    >
                      <ListItemIcon>
                        <Typography sx={{ fontSize: '1.2rem' }}>{country.flag}</Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={country.dialCode}
                        secondary={country.name}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </MenuItem>
                  ))}
              </Menu>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  disabled={isReadOnly}
                  helperText={
                    formData.email.trim() && !hasEmailIfFilled ? 'Enter a valid email address' : ''
                  }
                  error={Boolean(formData.email.trim()) && !hasEmailIfFilled}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={COUNTRY_OPTIONS}
                  getOptionLabel={(option) => option?.name || ''}
                  filterOptions={countryFilterOptions}
                  value={
                    COUNTRY_OPTIONS.find(
                      (country) =>
                        country?.name === formData.country ||
                        country?.code === formData.country ||
                        country?.name?.toLowerCase() === String(formData.country || '').toLowerCase()
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleFieldChange('country', newValue?.name || '');
                    handleFieldChange('state', '');
                    handleFieldChange('city', '');
                  }}
                  disabled={isReadOnly}
                  slotProps={drawerOverlays.autocompleteSlotProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Country"
                      required
                      helperText={formData.country ? '' : isReadOnly ? '' : 'Country is required'}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={stateOptions}
                  getOptionLabel={(option) => option?.name || ''}
                  filterOptions={stateFilterOptions}
                  value={
                    stateOptions.find(
                      (state) =>
                        state?.name === formData.state ||
                        state?.code === formData.state ||
                        state?.name?.toLowerCase() === String(formData.state || '').toLowerCase()
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleFieldChange('state', newValue?.name || '');
                    handleFieldChange('city', '');
                  }}
                  disabled={isReadOnly || !selectedCountryObj}
                  slotProps={drawerOverlays.autocompleteSlotProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="State"
                      required
                      helperText={
                        formData.state
                          ? ''
                          : isReadOnly
                            ? ''
                            : selectedCountryObj
                              ? 'State is required'
                              : 'Select country first'
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={cityOptions}
                  getOptionLabel={(option) => (option != null ? String(option) : '')}
                  filterOptions={cityFilterOptions}
                  value={
                    cityOptions.find(
                      (city) =>
                        String(city || '').trim().toLowerCase() ===
                        String(formData.city || '').trim().toLowerCase()
                    ) || (formData.city ? formData.city : null)
                  }
                  onChange={(event, newValue) => {
                    handleFieldChange('city', newValue != null ? String(newValue) : '');
                  }}
                  disabled={isReadOnly || !selectedStateObj}
                  slotProps={drawerOverlays.autocompleteSlotProps}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="City"
                      required
                      helperText={
                        formData.city
                          ? ''
                          : isReadOnly
                            ? ''
                            : selectedStateObj
                              ? 'City is required'
                              : 'Select state first'
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => handleFieldChange('address', e.target.value)}
                  multiline
                  rows={2}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GST Number"
                  value={formData.gst_number}
                  onChange={(e) => handleFieldChange('gst_number', e.target.value)}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="FSSAI License"
                  value={formData.fssai_license_number}
                  onChange={(e) => handleFieldChange('fssai_license_number', e.target.value)}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: isDark ? '#e5e7eb' : '#111827' }}>
                    Type
                  </Typography>
                  <Typography component="span" sx={{ color: theme.palette.error.main, ml: 0.25, fontSize: '0.875rem' }}>
                    *
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {STOCK_TYPE_OPTIONS.map((option) => {
                    const selected = formData.type === option.value;
                    return (
                      <Chip
                        key={option.value}
                        label={option.label}
                        onClick={() => {
                          if (!isReadOnly) handleFieldChange('type', option.value);
                        }}
                        disabled={isReadOnly}
                        sx={{
                          backgroundColor: selected ? '#4ce1b6' : isDark ? alpha('#ffffff', 0.06) : '#f5f5f5',
                          color: selected ? '#000' : isDark ? '#ddd' : '#333',
                          border: `2px solid ${selected ? '#4ce1b6' : chipBorder}`,
                          fontWeight: selected ? 600 : 500,
                          fontSize: '0.85rem',
                          height: 34,
                          cursor: isReadOnly ? 'default' : 'pointer',
                          '&:hover': {
                            backgroundColor: selected ? '#3ab896' : isDark ? '#38373f' : '#e8e8e8',
                            borderColor: '#4ce1b6',
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#e5e7eb' : '#111827' }}>
                    Status
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                    Inactive
                  </Typography>
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                    disabled={isReadOnly}
                    color="primary"
                    inputProps={{ 'aria-label': 'Supplier active status' }}
                  />
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                    Active
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  onClick={() => setDrawerOpen(false)}
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                    color: isDark ? '#ddd' : '#666',
                    '&:hover': {
                      borderColor: '#4ce1b6',
                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                      color: '#4ce1b6',
                    },
                  }}
                >
                  {isReadOnly ? 'Close' : 'Cancel'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  fullWidth
                  disabled={isReadOnly || !isFormValid || saveLoading}
                  sx={{
                    backgroundColor: '#4ce1b6',
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#4ce1b6',
                      opacity: 0.9,
                      boxShadow: isDark ? '0 4px 12px rgba(76, 225, 182, 0.4)' : '0 4px 12px rgba(76, 225, 182, 0.3)',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: isDark ? '#37474f' : '#cfd8dc',
                      color: isDark ? '#90a4ae' : '#78909c',
                    },
                  }}
                >
                  {saveLoading ? 'Saving...' : 'Save'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleteLoading) return;
          handleDeleteCancel();
        }}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: isDark ? '#fff' : '#212121',
            fontWeight: 600,
            pb: 2,
          }}
        >
          Delete supplier
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: isDark ? '#ddd' : '#666',
              fontSize: '1rem',
            }}
          >
            Are you sure you want to delete the supplier{' '}
            <strong>&quot;{supplierToDelete?.name || '—'}&quot;</strong>?
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
          }}
        >
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading}
            sx={{
              color: isDark ? '#999' : '#757575',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              '&:disabled': {
                backgroundColor: isDark ? '#555' : '#ccc',
              },
            }}
          >
            {deleteLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StockSupplierSection;
